import Phaser from 'phaser'

/** Generated pixel-art DESK setups, built once per scene. Every seat is a desk
 *  a character sits/works at — the variants just differ in what's on the desk
 *  (monitors, laptop, lamp, plant…), so they all behave identically. */
export const DESK_VARIANTS = 5

export const deskTexKey = (v: number): string =>
  `go-desk-${((v % DESK_VARIANTS) + DESK_VARIANTS) % DESK_VARIANTS}`

// r: [x, y, w, h, radius?]  ·  e: [cx, cy, rx, ry]  ·  c: hex  ·  a: alpha
export type Op =
  | { r: [number, number, number, number, number?]; c: string; a?: number }
  | { e: [number, number, number, number]; c: string; a?: number }

// Palette
const WD_OUT = '#2a1c12'
const WD_SH = '#5c3c22'
const WD = '#7a5230'
const WD_HI = '#b9874f'
const MT_OUT = '#10141a'
const MT = '#23272f'
const SCR = '#123047'
const SCR_HI = '#2f7bb0'
const SCR_UI = '#54a6dd'
const KEY = '#2b3038'
const MUG = '#c9735a'
const POT = '#9c5a3a'
const LEAF = '#3f7a3a'
const LEAF_HI = '#52a04a'
const LAMP = '#d9b25a'
const PAPER = '#dfe2e6'
const HEAD = '#d9d2c4'

// Shared desk surface + legs + shadow (drawn under the gear).
const BASE: Op[] = [
  { e: [14, 21, 12, 3], c: '#000000', a: 0.22 },
  { r: [4, 14, 4, 7, 1], c: WD_OUT },
  { r: [20, 14, 4, 7, 1], c: WD_OUT },
  { r: [5, 15, 2, 6], c: WD_SH },
  { r: [21, 15, 2, 6], c: WD_SH },
  { r: [1, 11, 26, 5, 2], c: WD_OUT },
  { r: [2, 12, 24, 3, 1], c: WD },
  { r: [2, 12, 24, 1], c: WD_HI },
  { r: [2, 14, 24, 1], c: WD_SH },
]

const monitor = (x: number): Op[] => [
  { r: [x, 1, 12, 9, 1], c: MT_OUT },
  { r: [x + 1, 2, 10, 6], c: SCR },
  { r: [x + 1, 2, 10, 1], c: SCR_HI },
  { r: [x + 3, 4, 5, 1], c: SCR_UI },
  { r: [x + 2, 6, 8, 1], c: '#1d4a6b' },
  { r: [x + 5, 9, 2, 3], c: MT },
]

const VARIANTS: Op[][] = [
  // 0 — single monitor + keyboard + mug
  [
    ...monitor(8),
    { r: [8, 12, 9, 2, 1], c: KEY },
    { r: [8, 12, 9, 1], c: '#3b424d' },
    { r: [21, 8, 4, 4, 1], c: WD_OUT },
    { r: [21, 8, 3, 3, 1], c: MUG },
  ],
  // 1 — dual monitors
  [...monitor(2), ...monitor(15), { r: [10, 12, 8, 2, 1], c: KEY }],
  // 2 — laptop + plant
  [
    { r: [7, 4, 12, 7, 1], c: MT_OUT },
    { r: [8, 5, 10, 4], c: SCR },
    { r: [8, 5, 10, 1], c: SCR_HI },
    { r: [6, 10, 14, 2, 1], c: '#2b313b' },
    { r: [21, 8, 5, 4, 1], c: POT },
    { e: [23, 5, 4, 4], c: LEAF },
    { e: [23, 4, 3, 2], c: LEAF_HI },
  ],
  // 3 — monitor + desk lamp + papers
  [
    ...monitor(10),
    { r: [3, 3, 2, 9], c: '#3a3f48' },
    { r: [3, 3, 6, 2, 1], c: LAMP },
    { r: [7, 4, 3, 2, 1], c: '#f0d27a' },
    { r: [21, 11, 5, 2, 1], c: WD_OUT },
    { r: [21, 11, 5, 1], c: PAPER },
  ],
  // 4 — ultrawide monitor + coffee
  [
    { r: [4, 3, 20, 7, 1], c: MT_OUT },
    { r: [5, 4, 18, 5], c: SCR },
    { r: [5, 4, 18, 1], c: SCR_HI },
    { r: [6, 6, 9, 1], c: SCR_UI },
    { r: [13, 9, 2, 3], c: MT },
    { r: [2, 9, 3, 3, 1], c: HEAD },
  ],
]

export const toInt = (hex: string): number =>
  Phaser.Display.Color.HexStringToColor(hex).color

export function drawOps(g: Phaser.GameObjects.Graphics, ops: Op[]): void {
  for (const op of ops) {
    g.fillStyle(toInt(op.c), op.a ?? 1)
    if ('r' in op) {
      const [x, y, w, h, r] = op.r
      if (r) g.fillRoundedRect(x, y, w, h, r)
      else g.fillRect(x, y, w, h)
    } else {
      const [cx, cy, rx, ry] = op.e
      g.fillEllipse(cx, cy, rx * 2, ry * 2)
    }
  }
}

export function buildFurnitureTextures(scene: Phaser.Scene): void {
  for (let i = 0; i < DESK_VARIANTS; i += 1) {
    const key = deskTexKey(i)
    if (scene.textures.exists(key)) continue
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    drawOps(g, [...BASE, ...VARIANTS[i]])
    g.generateTexture(key, 28, 22)
    g.destroy()
  }
}

// ============================================================================
// CONFERENCE-ROOM TEXTURES (same Op idiom + toInt/drawOps as the desks).
// New palette consts are MR_*-prefixed so they never clash with desk names.
// ============================================================================
const MR_CH_OUT = '#1b2a36'
const MR_CH = '#33536b'
const MR_CH_HI = '#5e8aa8'
const MR_CH_SH = '#21384a'
const MR_CH_LEG = '#11171d'
const MR_FRM = '#0c0f14'
const MR_FRM_HI = '#3a4250'
const MR_STEEL = '#9aa6b0'
const MR_STEEL_HI = '#cdd6dc'
const MR_STEEL_SH = '#5d6770'
const MR_WATER = '#7fc4d6'
const MR_LEAF_DK = '#2f5e2c'
const MR_WOV = '#c9a06a'
const MR_WOV_HI = '#e3c490'
const MR_WOV_SH = '#a07a44'

/** Exported texture keys for the meeting-room sprites. */
export const MEETING_TEX = {
  table: 'go-mr-table',
  screen: 'go-mr-screen',
  plant: 'go-mr-plant',
  cooler: 'go-mr-cooler',
} as const

// CONFERENCE TABLE (+8 chairs baked in) -> generateTexture(table, 132, 64)
const MR_TABLE_OPS: Op[] = [
  { e: [66, 50, 58, 9], c: '#000000', a: 0.22 },
  ...[34, 64, 94].flatMap((x): Op[] => [
    { r: [x - 8, 8, 16, 11, 2], c: MR_CH_OUT },
    { r: [x - 7, 9, 14, 8, 1], c: MR_CH },
    { r: [x - 7, 9, 14, 1], c: MR_CH_HI },
    { r: [x - 7, 16, 14, 1], c: MR_CH_SH },
    { r: [x - 6, 17, 12, 4, 1], c: MR_CH_SH },
  ]),
  { r: [6, 26, 11, 14, 2], c: MR_CH_OUT },
  { r: [7, 27, 8, 12, 1], c: MR_CH },
  { r: [7, 27, 8, 1], c: MR_CH_HI },
  { r: [7, 38, 8, 1], c: MR_CH_SH },
  { r: [13, 29, 5, 8, 1], c: MR_CH_SH },
  { r: [115, 26, 11, 14, 2], c: MR_CH_OUT },
  { r: [117, 27, 8, 12, 1], c: MR_CH },
  { r: [117, 27, 8, 1], c: MR_CH_HI },
  { r: [117, 38, 8, 1], c: MR_CH_SH },
  { r: [114, 29, 5, 8, 1], c: MR_CH_SH },
  { r: [16, 19, 100, 27, 12], c: WD_OUT },
  { r: [18, 20, 96, 24, 10], c: WD_SH },
  { r: [19, 20, 94, 22, 10], c: WD },
  { r: [22, 21, 88, 3, 2], c: WD_HI },
  { r: [22, 41, 88, 2, 1], c: WD_OUT },
  { r: [30, 26, 72, 11, 5], c: WD_SH, a: 0.5 },
  { r: [34, 28, 64, 2, 1], c: WD_HI, a: 0.35 },
  { e: [66, 32, 6, 4], c: '#000000', a: 0.18 },
  { r: [60, 28, 12, 7, 3], c: MT_OUT },
  { r: [61, 29, 10, 4, 2], c: MT },
  { e: [66, 31, 3, 2], c: SCR_HI },
  { r: [38, 30, 7, 5, 1], c: WD_OUT },
  { r: [38, 30, 7, 1], c: PAPER },
  { r: [87, 30, 7, 5, 1], c: WD_OUT },
  { r: [87, 30, 7, 1], c: PAPER },
  ...[34, 64, 94].flatMap((x): Op[] => [
    { e: [x, 60, 8, 2], c: '#000000', a: 0.2 },
    { r: [x - 8, 43, 16, 12, 2], c: MR_CH_OUT },
    { r: [x - 7, 44, 14, 8, 1], c: MR_CH },
    { r: [x - 7, 44, 14, 1], c: MR_CH_HI },
    { r: [x - 7, 51, 14, 1], c: MR_CH_SH },
    { r: [x - 6, 52, 12, 4, 1], c: MR_CH_SH },
    { r: [x - 1, 55, 2, 5], c: MR_CH_LEG },
    { r: [x - 5, 59, 10, 2, 1], c: MR_CH_LEG },
  ]),
]

// WALL SCREEN -> generateTexture(screen, 56, 30)
const MR_SCREEN_OPS: Op[] = [
  { r: [0, 1, 56, 26, 2], c: MR_FRM },
  { r: [1, 1, 54, 1], c: MR_FRM_HI },
  { r: [2, 3, 52, 21], c: SCR },
  { r: [2, 3, 52, 2], c: SCR_HI },
  { r: [6, 7, 30, 3, 1], c: SCR_UI },
  { r: [6, 13, 8, 7, 1], c: '#1d4a6b' },
  { r: [16, 11, 8, 9, 1], c: SCR_HI },
  { r: [26, 15, 8, 5, 1], c: '#1d4a6b' },
  { r: [40, 8, 12, 12, 1], c: '#0e2538' },
  { r: [42, 11, 8, 1], c: SCR_UI },
  { r: [42, 14, 6, 1], c: SCR_UI, a: 0.6 },
  { r: [2, 24, 52, 1], c: '#08182a' },
  { r: [10, 27, 36, 2, 1], c: MT_OUT },
  { r: [10, 27, 36, 1], c: MR_FRM_HI },
]

// PLANT (monstera in a woven pot) -> generateTexture(plant, 26, 36)
const MR_PLANT_OPS: Op[] = [
  { e: [13, 34, 8, 2], c: '#000000', a: 0.2 },
  { r: [6, 24, 14, 11, 2], c: MR_WOV_SH },
  { r: [6, 24, 14, 10, 2], c: MR_WOV },
  { r: [6, 24, 14, 1], c: MR_WOV_HI },
  { r: [7, 27, 12, 1], c: MR_WOV_SH, a: 0.6 },
  { r: [7, 30, 12, 1], c: MR_WOV_SH, a: 0.6 },
  { r: [8, 25, 1, 9], c: MR_WOV_HI, a: 0.4 },
  { r: [13, 25, 1, 9], c: MR_WOV_HI, a: 0.4 },
  { e: [7, 16, 5, 6], c: MR_LEAF_DK },
  { e: [7, 16, 4, 5], c: LEAF },
  { e: [7, 14, 3, 3], c: LEAF_HI, a: 0.8 },
  { e: [19, 15, 5, 6], c: MR_LEAF_DK },
  { e: [19, 15, 4, 5], c: LEAF },
  { e: [19, 13, 3, 3], c: LEAF_HI, a: 0.8 },
  { e: [13, 9, 6, 8], c: MR_LEAF_DK },
  { e: [13, 9, 5, 7], c: LEAF },
  { e: [13, 6, 3, 4], c: LEAF_HI, a: 0.85 },
  { r: [13, 4, 1, 9], c: MR_LEAF_DK },
  { r: [10, 10, 1, 5], c: MR_LEAF_DK, a: 0.7 },
  { r: [16, 9, 1, 5], c: MR_LEAF_DK, a: 0.7 },
  { r: [6, 16, 1, 4], c: MR_LEAF_DK, a: 0.6 },
  { r: [20, 15, 1, 4], c: MR_LEAF_DK, a: 0.6 },
  { r: [12, 16, 2, 9], c: '#5e7a3a' },
]

// WATER COOLER -> generateTexture(cooler, 16, 30)
const MR_COOLER_OPS: Op[] = [
  { e: [8, 28, 6, 2], c: '#000000', a: 0.22 },
  { r: [3, 1, 10, 10, 2], c: '#5fa8bb' },
  { r: [4, 2, 8, 8, 2], c: MR_WATER },
  { r: [4, 2, 8, 1], c: '#b6e6f0' },
  { r: [6, 10, 4, 3, 1], c: '#4f93a4' },
  { r: [2, 12, 12, 16, 2], c: MR_STEEL_SH },
  { r: [3, 13, 10, 14, 1], c: MR_STEEL },
  { r: [3, 13, 2, 14], c: MR_STEEL_HI },
  { r: [11, 13, 2, 14], c: MR_STEEL_SH },
  { r: [5, 17, 6, 3, 1], c: '#0c0f14' },
  { r: [6, 18, 2, 2], c: '#d05a48' },
  { r: [9, 18, 2, 2], c: SCR_HI },
  { r: [3, 25, 10, 1], c: MR_STEEL_HI },
]

/** Build the four meeting-room textures once per scene (idempotent). */
export function buildMeetingTextures(scene: Phaser.Scene): void {
  const defs: Array<[string, Op[], number, number]> = [
    [MEETING_TEX.table, MR_TABLE_OPS, 132, 64],
    [MEETING_TEX.screen, MR_SCREEN_OPS, 56, 30],
    [MEETING_TEX.plant, MR_PLANT_OPS, 26, 36],
    [MEETING_TEX.cooler, MR_COOLER_OPS, 16, 30],
  ]
  for (const [key, ops, w, h] of defs) {
    if (scene.textures.exists(key)) continue
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    drawOps(g, ops)
    g.generateTexture(key, w, h)
    g.destroy()
  }
}

// ============================================================================
// LOUNGE (water-cooler social spot) textures — same Op idiom + drawOps/toInt as
// everything else. The cooler itself reuses MEETING_TEX.cooler; here we add a
// soft round rug + a little stool so the spot reads as a social lounge, not a
// desk cluster. LG_*-prefixed palette so it never clashes with desk/MR names.
// ============================================================================
const LG_RUG_OUT = '#3a2c44'
const LG_RUG = '#534064'
const LG_RUG_HI = '#6f567f'
const LG_RUG_RING = '#caa15e'
const LG_RUG_RING2 = '#7fb0c4'
const LG_ST_OUT = '#1d242c'
const LG_ST_TOP = '#b9874f'
const LG_ST_TOP_HI = '#d4a96b'
const LG_ST_LEG = '#11171d'

/** Lounge texture keys (the cooler reuses MEETING_TEX.cooler). */
export const LOUNGE_TEX = {
  rug: 'go-lg-rug',
  stool: 'go-lg-stool',
} as const

// Soft round social RUG -> generateTexture(rug, 120, 84)
const LG_RUG_OPS: Op[] = [
  { e: [60, 44, 58, 38], c: '#000000', a: 0.18 },
  { e: [60, 42, 56, 36], c: LG_RUG_OUT },
  { e: [60, 42, 53, 33], c: LG_RUG },
  { e: [60, 40, 50, 30], c: LG_RUG_HI, a: 0.5 },
  { e: [60, 42, 40, 24], c: LG_RUG_RING, a: 0.35 },
  { e: [60, 42, 39, 23], c: LG_RUG, a: 0.9 },
  { e: [60, 42, 26, 16], c: LG_RUG_RING2, a: 0.28 },
  { e: [60, 42, 25, 15], c: LG_RUG, a: 0.85 },
  { e: [60, 41, 12, 7], c: LG_RUG_HI, a: 0.5 },
]

// Little STOOL (top-down-ish, matches desk wood) -> generateTexture(stool, 22, 22)
const LG_STOOL_OPS: Op[] = [
  { e: [11, 18, 9, 3], c: '#000000', a: 0.22 },
  { r: [5, 14, 2, 5], c: LG_ST_LEG },
  { r: [15, 14, 2, 5], c: LG_ST_LEG },
  { e: [11, 11, 9, 6], c: LG_ST_OUT },
  { e: [11, 10, 8, 5], c: LG_ST_TOP },
  { e: [11, 9, 6, 3], c: LG_ST_TOP_HI, a: 0.8 },
]

/** Build the lounge textures once per scene (idempotent). Call after
 *  buildMeetingTextures in OfficeScene.create. */
export function buildLoungeTextures(scene: Phaser.Scene): void {
  const defs: Array<[string, Op[], number, number]> = [
    [LOUNGE_TEX.rug, LG_RUG_OPS, 120, 84],
    [LOUNGE_TEX.stool, LG_STOOL_OPS, 22, 22],
  ]
  for (const [key, ops, w, h] of defs) {
    if (scene.textures.exists(key)) continue
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    drawOps(g, ops)
    g.generateTexture(key, w, h)
    g.destroy()
  }
}

// ============================================================================
// COMMONS (staff common area) textures — the room for colleagues you share no
// active pulse with. Lounge language, not desk language: armchairs instead of
// workstations, a coffee table, a floor lamp. CM_*-prefixed palette.
// ============================================================================
const CM_CH_OUT = '#231318'
const CM_CH = '#8a4638'
const CM_CH_HI = '#b2604a'
const CM_CH_SEAT = '#a2543f'
const CM_CH_LEG = '#171310'
const CM_TBL_TOP = '#8a6a42'
const CM_TBL_HI = '#b28c58'
const CM_LAMP_POLE = '#3a3f48'
const CM_LAMP_SHADE = '#d9b25a'
const CM_LAMP_GLOW = '#f0d27a'

export const COMMONS_TEX = {
  /** A plush terracotta armchair (drawn BEHIND the person — they sit in it). */
  chair: 'go-cm-chair',
  /** Low oval coffee table with mugs + a magazine. */
  table: 'go-cm-table',
  /** Warm floor lamp for the corners. */
  lamp: 'go-cm-lamp',
} as const

// ARMCHAIR -> generateTexture(chair, 28, 24). High back + arms; the seat is
// left open so a character rendered in front reads as sitting in it.
const CM_CHAIR_OPS: Op[] = [
  { e: [14, 21, 12, 3], c: '#000000', a: 0.22 },
  { r: [3, 19, 3, 4], c: CM_CH_LEG },
  { r: [22, 19, 3, 4], c: CM_CH_LEG },
  // Back rest.
  { r: [4, 1, 20, 12, 4], c: CM_CH_OUT },
  { r: [5, 2, 18, 10, 3], c: CM_CH },
  { r: [5, 2, 18, 2, 2], c: CM_CH_HI },
  // Arms.
  { r: [1, 8, 5, 12, 2], c: CM_CH_OUT },
  { r: [2, 9, 3, 10, 2], c: CM_CH_HI },
  { r: [22, 8, 5, 12, 2], c: CM_CH_OUT },
  { r: [23, 9, 3, 10, 2], c: CM_CH_HI },
  // Seat cushion front lip.
  { r: [5, 16, 18, 5, 2], c: CM_CH_OUT },
  { r: [6, 17, 16, 3, 2], c: CM_CH_SEAT },
]

// COFFEE TABLE -> generateTexture(table, 36, 26)
const CM_TABLE_OPS: Op[] = [
  { e: [18, 22, 15, 3], c: '#000000', a: 0.2 },
  { r: [6, 16, 3, 6], c: CM_CH_LEG },
  { r: [27, 16, 3, 6], c: CM_CH_LEG },
  { e: [18, 11, 16, 7], c: WD_OUT },
  { e: [18, 10, 15, 6], c: CM_TBL_TOP },
  { e: [18, 9, 13, 4], c: CM_TBL_HI, a: 0.55 },
  // Two mugs + a magazine.
  { r: [10, 6, 4, 4, 1], c: WD_OUT },
  { r: [10, 6, 3, 3, 1], c: MUG },
  { r: [22, 5, 4, 4, 1], c: WD_OUT },
  { r: [22, 5, 3, 3, 1], c: '#5e8aa8' },
  { r: [15, 10, 7, 4, 1], c: PAPER },
  { r: [16, 11, 5, 1], c: '#b5654a' },
]

// FLOOR LAMP -> generateTexture(lamp, 16, 34)
const CM_LAMP_OPS: Op[] = [
  { e: [8, 32, 6, 2], c: '#000000', a: 0.22 },
  { r: [4, 30, 8, 2, 1], c: CM_LAMP_POLE },
  { r: [7, 10, 2, 21], c: CM_LAMP_POLE },
  { r: [2, 1, 12, 9, 2], c: CM_LAMP_SHADE },
  { r: [3, 2, 10, 3, 1], c: CM_LAMP_GLOW },
  { r: [2, 9, 12, 1], c: '#a5823a' },
  { e: [8, 12, 4, 2], c: CM_LAMP_GLOW, a: 0.4 },
]

/** Build the Commons textures once per scene (idempotent). */
export function buildCommonsTextures(scene: Phaser.Scene): void {
  const defs: Array<[string, Op[], number, number]> = [
    [COMMONS_TEX.chair, CM_CHAIR_OPS, 28, 24],
    [COMMONS_TEX.table, CM_TABLE_OPS, 36, 26],
    [COMMONS_TEX.lamp, CM_LAMP_OPS, 16, 34],
  ]
  for (const [key, ops, w, h] of defs) {
    if (scene.textures.exists(key)) continue
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    drawOps(g, ops)
    g.generateTexture(key, w, h)
    g.destroy()
  }
}

// ============================================================================
// AGENT BAY textures — where agents you share no pulse with idle. Machine-room
// language: each agent stands on a glowing charge pad; a server tower and a
// holo core dress the room. AB_*-prefixed palette (agent violet + visor cyan).
// ============================================================================
const AB_PAD_OUT = '#101322'
const AB_PAD = '#1d2138'
const AB_PAD_HI = '#2b3050'
const AB_GLOW = '#8b7ff0'
const AB_CYAN = '#7ff0ff'
const AB_STEEL = '#2a2f3a'
const AB_STEEL_HI = '#3c4455'

export const AGENTBAY_TEX = {
  /** Charge pad drawn UNDER each idle agent (glow ring + power feed). */
  pad: 'go-ab-pad',
  /** Slim server tower with LED rows, for the bay's back wall. */
  tower: 'go-ab-tower',
  /** Holographic core on a pedestal — the bay's centrepiece. */
  holo: 'go-ab-holo',
} as const

// CHARGE PAD -> generateTexture(pad, 30, 16). Flat, so it sits under the feet.
const AB_PAD_OPS: Op[] = [
  { e: [15, 9, 14, 6], c: '#000000', a: 0.25 },
  { e: [15, 8, 13, 6], c: AB_PAD_OUT },
  { e: [15, 8, 11, 5], c: AB_PAD },
  { e: [15, 8, 9, 4], c: AB_GLOW, a: 0.35 },
  { e: [15, 8, 7, 3], c: AB_PAD, a: 0.9 },
  { e: [15, 8, 3, 1.5], c: AB_CYAN, a: 0.5 },
  // Power feed nub at the back.
  { r: [13, 0, 4, 3, 1], c: AB_STEEL },
  { r: [14, 1, 2, 1], c: AB_GLOW, a: 0.8 },
]

// SERVER TOWER -> generateTexture(tower, 22, 36)
const AB_TOWER_OPS: Op[] = [
  { e: [11, 34, 9, 2], c: '#000000', a: 0.25 },
  { r: [2, 2, 18, 32, 2], c: AB_PAD_OUT },
  { r: [3, 3, 16, 30, 2], c: AB_STEEL },
  { r: [3, 3, 16, 2], c: AB_STEEL_HI },
  // Blade slots.
  ...[7, 13, 19, 25].flatMap((y): Op[] => [
    { r: [5, y, 12, 4, 1], c: '#161a26' },
    { r: [6, y + 1, 6, 1], c: AB_STEEL_HI, a: 0.5 },
  ]),
  // LEDs (the scene layers blinking dots on top of these).
  { r: [15, 8, 1.5, 1.5], c: '#54d18c' },
  { r: [15, 14, 1.5, 1.5], c: AB_CYAN },
  { r: [15, 20, 1.5, 1.5], c: '#54d18c' },
  { r: [15, 26, 1.5, 1.5], c: AB_GLOW },
  // Vent grille at the bottom.
  { r: [5, 30, 12, 1], c: '#161a26' },
]

// HOLO CORE -> generateTexture(holo, 26, 34). Pedestal + floating orb; the
// scene adds a pulsing glow behind the orb.
const AB_HOLO_OPS: Op[] = [
  { e: [13, 32, 9, 2], c: '#000000', a: 0.25 },
  { r: [7, 24, 12, 8, 2], c: AB_PAD_OUT },
  { r: [8, 25, 10, 6, 2], c: AB_STEEL },
  { r: [8, 25, 10, 1], c: AB_STEEL_HI },
  { r: [11, 22, 4, 3, 1], c: AB_PAD_HI },
  // Projection beam.
  { r: [10, 12, 6, 10], c: AB_CYAN, a: 0.12 },
  // The orb: layered violet/cyan sphere.
  { e: [13, 8, 7, 7], c: AB_GLOW, a: 0.3 },
  { e: [13, 8, 5, 5], c: AB_GLOW, a: 0.7 },
  { e: [13, 8, 3.5, 3.5], c: '#a89dff' },
  { e: [12, 7, 1.5, 1.5], c: '#e6e1ff' },
  // Orbit ring.
  { e: [13, 9, 7, 2], c: AB_CYAN, a: 0.35 },
]

/** Build the Agent Bay textures once per scene (idempotent). */
export function buildAgentBayTextures(scene: Phaser.Scene): void {
  const defs: Array<[string, Op[], number, number]> = [
    [AGENTBAY_TEX.pad, AB_PAD_OPS, 30, 16],
    [AGENTBAY_TEX.tower, AB_TOWER_OPS, 22, 36],
    [AGENTBAY_TEX.holo, AB_HOLO_OPS, 26, 34],
  ]
  for (const [key, ops, w, h] of defs) {
    if (scene.textures.exists(key)) continue
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    drawOps(g, ops)
    g.generateTexture(key, w, h)
    g.destroy()
  }
}
